// src/pages/SignupPage.jsx

import React, { useState } from 'react';
import {
  Container,
  Typography,
  TextField,
  Box,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Paper,
  FormControlLabel,
  Checkbox,
  Alert,
  InputAdornment,
} from '@mui/material';
import { QrCode2, Logout, ArrowBack, Dashboard, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoadingButton from './LoadingButton';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    isAdmin: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const isAdmin = JSON.parse(sessionStorage.getItem('user'))?.isAdmin || false; // Get isAdmin status

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Password validation - minimum 8 characters
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: formData.email.toLowerCase().trim(), // Case insensitive email
          password: formData.password,
          isAdmin: formData.isAdmin,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 409 || (data.detail && data.detail.includes('already exists'))) {
          throw new Error('A user with this email address already exists. Please use a different email.');
        } else if (response.status === 422) {
          throw new Error('Invalid email format. Please enter a valid email address.');
        } else if (response.status === 400) {
          throw new Error(data.detail || 'Invalid user data. Please check your inputs.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to create users.');
        } else {
          throw new Error(data.detail || 'Failed to create user. Please try again.');
        }
      }

      setSuccess(true);
      // Clear form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        isAdmin: false,
      });
      
      // Navigate after a short delay to show the success message
      setTimeout(() => {
        navigate('/admin-dashboard');
      }, 2000);
    } catch (error) {
      setError(error.message || 'Failed to create user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.checked,
    }));
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    sessionStorage.removeItem('user');
    navigate('/login');
  };

  const handleAdminDashboard = () => {
    navigate('/admin-dashboard');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
              <AppBar position="static" color="primary" elevation={0} sx={{ bgcolor: '#1F425C' }}>
        <Toolbar>
                      <img src="/HINDALCO.NS_BIG.png" alt="Hindalco" style={{ marginRight: '16px', height: '32px', width: 'auto' }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#fff', fontWeight: 500 }}>
            Hindalco QR Management
          </Typography>
          {isAdmin && (
            <Button
              color="inherit"
              startIcon={<Dashboard />}
              onClick={handleAdminDashboard}
              sx={{
                ml: 2,
                color: '#fff',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Admin Dashboard
            </Button>
          )}
          <Button
            color="inherit"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{
              ml: 2,
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            Logout
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ mt: 4, mb: 12, pb: 4, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={2}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
            width: '100%', // Ensure it takes full width within its grid item
            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '0 8px 40px rgba(0, 0, 0, 0.1)',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                color: '#1F425C',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'scale(1.1)',
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ color: '#1F425C', fontWeight: 600 }}>
              Add New User
            </Typography>
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={loading}
              InputProps={{
                style: {
                  backgroundColor: '#fff !important',
                  color: '#000 !important',
                  WebkitTextFillColor: '#000 !important',
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#1F425C',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1F425C',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputBase-input': {
                  backgroundColor: '#fff !important',
                  WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                  WebkitTextFillColor: '#000 !important',
                  color: '#000 !important',
                  '&:hover': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                  '&:active': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                  '&:focus': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748b',
                  '&.Mui-focused': {
                    color: '#1F425C',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
              helperText="Password must be at least 8 characters long"
              InputProps={{
                style: {
                  backgroundColor: '#fff !important',
                  color: '#000 !important',
                  WebkitTextFillColor: '#000 !important',
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      sx={{ color: '#64748b' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#1F425C',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1F425C',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputBase-input': {
                  backgroundColor: '#fff !important',
                  WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                  WebkitTextFillColor: '#000 !important',
                  color: '#000 !important',
                  '&:hover': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                  '&:active': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                  '&:focus': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748b',
                  '&.Mui-focused': {
                    color: '#1F425C',
                  },
                },
              }}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={loading}
              InputProps={{
                style: {
                  backgroundColor: '#fff !important',
                  color: '#000 !important',
                  WebkitTextFillColor: '#000 !important',
                },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      sx={{ color: '#64748b' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                mt: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: '#fff',
                  '&:hover fieldset': {
                    borderColor: '#1F425C',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1F425C',
                    borderWidth: 2,
                  },
                },
                '& .MuiInputBase-input': {
                  backgroundColor: '#fff !important',
                  WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                  WebkitTextFillColor: '#000 !important',
                  color: '#000 !important',
                  '&:hover': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                  '&:active': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                  '&:focus': {
                    backgroundColor: '#fff !important',
                    WebkitBoxShadow: '0 0 0 1000px #fff inset !important',
                    WebkitTextFillColor: '#000 !important',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: '#64748b',
                  '&.Mui-focused': {
                    color: '#1F425C',
                  },
                },
              }}
            />
            {isAdmin && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isAdmin}
                    onChange={handleCheckboxChange}
                    name="isAdmin"
                    color="primary"
                    disabled={loading}
                    sx={{
                      color: '#1F425C',
                      '&.Mui-checked': {
                        color: '#1F425C',
                      },
                    }}
                  />
                }
                label="Admin User"
                sx={{ mt: 2, mb: 1 }}
              />
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 3, mb: 2, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mt: 3, mb: 2, borderRadius: 2 }}>
                User created successfully!
              </Alert>
            )}
            <LoadingButton
              type="submit"
              fullWidth
              variant="contained"
              loading={loading}
              disabled={loading || !formData.email || !formData.password || !formData.confirmPassword}
              sx={{
                mt: 3,
                py: 1.5,
                borderRadius: 2,
                bgcolor: '#1F425C',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(44, 62, 80, 0.2)',
                '&:hover': {
                  bgcolor: '#1a252f',
                  boxShadow: '0 6px 20px rgba(44, 62, 80, 0.3)',
                  transform: 'translateY(-2px)',
                },
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:disabled': {
                  bgcolor: '#bdc3c7',
                },
              }}
            >
              Create User
            </LoadingButton>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignupPage;